import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import EnrollmentPopover from "./components/EnrollmentPopover";
import { openStudentGradeReport } from "../../_services/reports.service";

interface SearchResultsProps {
  studentList: any[];
  searchDisplayBeans: any[];
  permissions: any;
  enquiryEnrollmentsMap: Record<number, any[]>;
  selectedEnquiryIds: number[];
  onToggleSelect: (enquiryId: number) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  isProgramBased: boolean;
  onOpenDrawer?: (enrollmentId: number, photoPath?: string | null) => void;
  onCreateEnrollment?: (enquiryId: number) => void;
  onDeleteEnquiry?: (enquiryId: number) => Promise<void> | void;
}

/* ─── Shared styles ─── */

const headerCellStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "var(--surface-page)",
  color: "var(--text-body)",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "10px 12px",
  borderBottom: "1px solid var(--border-default)",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const bodyCellStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: "0.82rem",
  color: "var(--text-strong)",
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
};

const iconBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  borderRadius: 6,
  textDecoration: "none",
  transition: "background-color .12s",
};

const SearchResults: React.FC<SearchResultsProps> = ({
  studentList,
  searchDisplayBeans,
  permissions,
  enquiryEnrollmentsMap,
  selectedEnquiryIds,
  onToggleSelect,
  onToggleSelectAll,
  allSelected,
  isProgramBased,
  onOpenDrawer,
  onCreateEnrollment,
  onDeleteEnquiry,
}) => {
  void isProgramBased;
  const [popoverEnquiryId, setPopoverEnquiryId] = useState<number | null>(null);
  const [popoverTarget, setPopoverTarget] = useState<HTMLElement | null>(null);
  const [reportLoadingId, setReportLoadingId] = useState<number | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [deleteTargetEnquiryId, setDeleteTargetEnquiryId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");

  const deleteTargetStudent =
    deleteTargetEnquiryId != null
      ? studentList.find((s) => s.enquiryId === deleteTargetEnquiryId)
      : null;
  const deleteTargetEnrollments =
    deleteTargetEnquiryId != null
      ? enquiryEnrollmentsMap[deleteTargetEnquiryId] || []
      : [];

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTargetEnquiryId(null);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (deleteTargetEnquiryId == null || !onDeleteEnquiry) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await onDeleteEnquiry(deleteTargetEnquiryId);
      setDeleteTargetEnquiryId(null);
    } catch (err: any) {
      setDeleteError(
        err?.response?.data?.error?.message ||
          err?.message ||
          "Failed to delete enquiry",
      );
    } finally {
      setDeleting(false);
    }
  };

  if (!studentList || studentList.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 20px",
          color: "var(--text-muted)",
          background: "var(--surface-card)",
          borderRadius: 12,
          border: "1px solid var(--border-default)",
        }}
      >
        <div style={{ fontSize: 36, color: "var(--text-faint)", marginBottom: 12 }}>
          <i className="fa fa-search" />
        </div>
        <div style={{ fontWeight: 600, color: "var(--text-strong)", marginBottom: 4 }}>
          No students found
        </div>
        <div style={{ fontSize: "0.85rem" }}>
          Try adjusting your search criteria above.
        </div>
      </div>
    );
  }

  const getFieldValue = (student: any, fieldName: string) => {
    const map: Record<string, string> = {
      studentCode: "studentCode",
      enrollmentStatus: "enrollmentStatus",
      enquirySchool: "enquirySchool",
      enrollmentSchool: "enrollmentSchool",
      batch: "batch",
      mobile: "mobile",
      email: "email",
      enquiryDate: "enquiryDate",
      dateOfBirth: "dateOfBirth",
      gender: "gender",
      counsellor: "counsellor",
      webForm: "webForm",
      remarks: "remarks",
      aadharNumber: "aadharNumber",
      nationality: "nationality",
      alternateEmail: "alternateEmail",
      punchingCode: "punchingCode",
      studentUserName: "studentUserName",
      studentPassword: "studentPassword",
      parentUserName: "parentUserName",
      parentPassword: "parentPassword",
      enquiryCourse: "enquiryCourse",
      enrollmentCourse: "enrollmentCourse",
      enquiryProgram: "enquiryProgram",
      enrollmentProgram: "enrollmentProgram",
      term: "term",
      photoPath: "photoPath",
    };
    const key = map[fieldName] || fieldName;
    return student[key] || "";
  };

  const handleEyeClick = (e: React.MouseEvent<HTMLElement>, enquiryId: number) => {
    e.stopPropagation();
    if (popoverEnquiryId === enquiryId) {
      setPopoverEnquiryId(null);
      setPopoverTarget(null);
    } else {
      setPopoverEnquiryId(enquiryId);
      setPopoverTarget(e.currentTarget);
    }
  };

  // Soft pastel pill tones — pick a palette per status string so dropouts,
  // pending, etc. don't all blend together. Falls back to slate for unknown.
  const statusPalette = (raw: string): { bg: string; fg: string; border: string } => {
    const s = String(raw || "").toLowerCase();
    if (!s) return { bg: "#fff7ed", fg: "#9a3412", border: "#fed7aa" }; // amber → "Enquiry" fallback
    if (s.includes("dropout") || s.includes("inactive") || s.includes("cancel")) {
      return { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" }; // red
    }
    if (s.includes("hold") || s.includes("pending") || s.includes("draft")) {
      return { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" }; // yellow
    }
    if (s.includes("regular") || s.includes("active") || s.includes("enroll")) {
      return { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" }; // emerald
    }
    return { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" }; // blue
  };

  const displayedColumns = searchDisplayBeans.filter((b) => b.name !== "photoPath");

  return (
    <div
      style={{
        background: "var(--surface-card)",
        borderRadius: 12,
        border: "1px solid var(--border-default)",
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "auto",
          }}
        >
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, width: 40, textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleSelectAll}
                  style={{ margin: 0, cursor: "pointer" }}
                />
              </th>
              <th style={headerCellStyle}>Enquiry</th>
              <th style={headerCellStyle}>Name</th>
              {displayedColumns.map((bean) => (
                <th
                  key={bean.name}
                  style={{
                    ...headerCellStyle,
                    minWidth: bean.field1 ? Number(bean.field1) : undefined,
                  }}
                >
                  {bean.displayLabel}
                </th>
              ))}
              {permissions.allowTheUserToDeleteEnquiry && (
                <th style={{ ...headerCellStyle, width: 70, textAlign: "center" }}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {studentList.map((student) => {
              const enquiryId = student.enquiryId;
              const enrollments = enquiryEnrollmentsMap[enquiryId] || [];
              const isSelected = selectedEnquiryIds.includes(enquiryId);
              const isHovered = hoveredRow === enquiryId;

              const rowBg = isSelected
                ? "#eff6ff"
                : isHovered
                  ? "#f8fafc"
                  : "#fff";

              return (
                <tr
                  key={enquiryId}
                  onMouseEnter={() => setHoveredRow(enquiryId)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    background: rowBg,
                    transition: "background-color .12s",
                  }}
                >
                  <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect(enquiryId)}
                      style={{ margin: 0, cursor: "pointer" }}
                    />
                  </td>

                  {/* Enquiry — avatar + ID + Draft pill */}
                  <td style={bodyCellStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {student.photoPath ? (
                        <img
                          src={student.photoPath}
                          alt=""
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "1px solid var(--border-default)",
                            background: "var(--surface-sunken)",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            background: "var(--surface-sunken)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--text-muted)",
                            fontSize: 15,
                          }}
                        >
                          <i className="fa fa-user" />
                        </div>
                      )}
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "var(--text-strong)" }}>
                          {enquiryId}
                        </span>
                        {student.isDraft && (
                          <span
                            style={{
                              fontSize: "0.65rem",
                              padding: "1px 6px",
                              borderRadius: 10,
                              background: "#fff7ed",
                              color: "#9a3412",
                              border: "1px solid #fed7aa",
                              fontWeight: 600,
                              alignSelf: "flex-start",
                            }}
                          >
                            DRAFT
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Name + action row + enrollment chips */}
                  <td style={{ ...bodyCellStyle, minWidth: 200 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <span style={{ fontWeight: 600, color: "var(--text-strong)", fontSize: "0.85rem" }}>
                        {student.name}
                      </span>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: 2,
                          background: "var(--surface-sunken)",
                          borderRadius: 6,
                          alignSelf: "flex-start",
                        }}
                      >
                        <a
                          href={
                            permissions.showWebFormEnquiryScreen
                              ? `/webform/${student.webformId || permissions.formIdToOpen}/enquiry/${enquiryId}?mode=view`
                              : `/enquiry/view/${enquiryId}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          title="View Enquiry"
                          style={{ ...iconBtnStyle, color: "var(--brand-primary-hover)" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--status-info-bg)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <i className="fa fa-search" style={{ fontSize: 11 }} />
                        </a>
                        {permissions.allowTheUserToEditEnquiry && (
                          <a
                            href={
                              permissions.showWebFormEnquiryScreen
                                ? `/webform/${student.webformId || permissions.formIdToOpen}/enquiry/${enquiryId}`
                                : `/enquiry/edit/${enquiryId}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            title="Edit Enquiry"
                            style={{ ...iconBtnStyle, color: "var(--brand-accent)" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--brand-primary-subtle)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            <i className="fa fa-pencil" style={{ fontSize: 11 }} />
                          </a>
                        )}
                        {enrollments.length > 0 && (
                          <span
                            title={`${enrollments.length} enrollment(s) — click to view`}
                            style={{ ...iconBtnStyle, color: "#0284c7", cursor: "pointer" }}
                            onClick={(e) => handleEyeClick(e, enquiryId)}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#e0f2fe")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            <i className="fa fa-eye" style={{ fontSize: 11 }} />
                          </span>
                        )}
                        {onCreateEnrollment && (
                          <span
                            title="Create new enrollment"
                            style={{ ...iconBtnStyle, color: "var(--status-success)", cursor: "pointer" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onCreateEnrollment(enquiryId);
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--status-success-bg)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            <i className="fa fa-plus" style={{ fontSize: 11 }} />
                          </span>
                        )}
                      </div>

                      {enrollments.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          {enrollments.map((enr) => {
                            const pal = statusPalette(enr.statusName);
                            return (
                              <div
                                key={enr.enrollmentId}
                                onClick={() => onOpenDrawer?.(enr.enrollmentId, student.photoPath)}
                                title="Click to quick-view this enrollment"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  fontSize: "0.7rem",
                                  cursor: "pointer",
                                  color: "var(--text-body)",
                                }}
                              >
                                {enr.studentCode && (
                                  <span style={{ fontWeight: 600, color: "var(--text-strong)" }}>
                                    {enr.studentCode}
                                  </span>
                                )}
                                {enr.statusName && (
                                  <span
                                    style={{
                                      padding: "1px 6px",
                                      borderRadius: 10,
                                      background: pal.bg,
                                      color: pal.fg,
                                      border: `1px solid ${pal.border}`,
                                      fontSize: "0.65rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {enr.statusName}
                                  </span>
                                )}
                                {enr.batchName && (
                                  <span style={{ color: "var(--text-muted)" }}>{enr.batchName}</span>
                                )}
                                {enr.batchId && (
                                  <span
                                    title="Grade Report"
                                    style={{ ...iconBtnStyle, width: 18, height: 18, color: "var(--brand-primary-hover)" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (reportLoadingId === enr.enrollmentId) return;
                                      setReportLoadingId(enr.enrollmentId);
                                      openStudentGradeReport(enr.enquiryId, enr.batchId)
                                        .then((r) => {
                                          if (!r.success) alert(r.message);
                                        })
                                        .catch((err) => alert(err.message || "Failed"))
                                        .finally(() => setReportLoadingId(null));
                                    }}
                                  >
                                    {reportLoadingId === enr.enrollmentId ? (
                                      <i className="fa fa-spinner fa-spin" style={{ fontSize: 9 }} />
                                    ) : (
                                      <i className="fa fa-file-text-o" style={{ fontSize: 9 }} />
                                    )}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Dynamic columns */}
                  {displayedColumns.map((bean) => {
                    const raw = getFieldValue(student, bean.name);
                    if (bean.name === "enrollmentStatus") {
                      const pal = statusPalette(raw);
                      const label = raw || "Enquiry";
                      return (
                        <td key={bean.name} style={bodyCellStyle}>
                          <span
                            style={{
                              padding: "3px 10px",
                              borderRadius: 12,
                              background: pal.bg,
                              color: pal.fg,
                              border: `1px solid ${pal.border}`,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                            }}
                          >
                            {label}
                          </span>
                        </td>
                      );
                    }
                    return (
                      <td key={bean.name} style={bodyCellStyle}>
                        <span style={{ color: raw ? "#0f172a" : "#94a3b8" }}>
                          {raw || "—"}
                        </span>
                      </td>
                    );
                  })}

                  {permissions.allowTheUserToDeleteEnquiry && (
                    <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                      <span
                        title="Delete Enquiry"
                        style={{
                          ...iconBtnStyle,
                          color: "var(--status-danger)",
                          cursor: "pointer",
                          width: 28,
                          height: 28,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteError("");
                          setDeleteTargetEnquiryId(enquiryId);
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--status-danger-bg)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <i className="fa fa-trash" style={{ fontSize: 13 }} />
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <EnrollmentPopover
        show={popoverEnquiryId != null}
        target={popoverTarget}
        enrollments={popoverEnquiryId ? enquiryEnrollmentsMap[popoverEnquiryId] || [] : []}
        onClose={() => {
          setPopoverEnquiryId(null);
          setPopoverTarget(null);
        }}
        onQuickView={(enrollmentId) => {
          const popoverStudent = studentList.find((s) => s.enquiryId === popoverEnquiryId);
          onOpenDrawer?.(enrollmentId, popoverStudent?.photoPath);
        }}
        enquiryId={popoverEnquiryId}
        onCreateEnrollment={onCreateEnrollment}
      />

      <Modal
        show={deleteTargetEnquiryId != null}
        onHide={closeDeleteModal}
        centered
        backdrop={deleting ? "static" : true}
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title style={{ fontSize: "1.05rem" }}>Delete Enquiry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: "0.9rem", color: "var(--text-strong)" }}>
            Are you sure you want to delete enquiry{" "}
            <strong>{deleteTargetEnquiryId}</strong>
            {deleteTargetStudent?.name ? (
              <>
                {" "}— <strong>{deleteTargetStudent.name}</strong>
              </>
            ) : null}
            ?
          </div>
          {deleteTargetEnrollments.length > 0 && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 10px",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                borderRadius: 6,
                fontSize: "0.8rem",
                color: "#9a3412",
              }}
            >
              This enquiry has {deleteTargetEnrollments.length} enrollment
              {deleteTargetEnrollments.length === 1 ? "" : "s"}. They will also be
              marked inactive.
            </div>
          )}
          <div
            style={{
              marginTop: 10,
              fontSize: "0.78rem",
              color: "var(--text-muted)",
            }}
          >
            The enquiry, its enrollments, and the linked login accounts will be
            set inactive. No other data is changed.
          </div>
          {deleteError && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 10px",
                background: "var(--status-danger-bg)",
                color: "var(--status-danger)",
                borderRadius: 6,
                fontSize: "0.8rem",
              }}
            >
              {deleteError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />
                Deleting…
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SearchResults;
